import { useEffect, useRef } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { BellRing, ChevronRight } from "lucide-react";
import { CASE_STATUS_LABEL, type Today as TodayData, type TodayApp } from "@metrickle/schema";
import { api } from "../lib/api";
import { usePageTitle } from "../lib/page-title";
import { fmtAgo, fmtMoney } from "../lib/format";
import { pushSupported, thisDeviceId, usePushStatus } from "../lib/push";
import { track } from "../lib/track";
import { Callout, Card, CardHeader, CardRowLink, Drawing, Empty, ErrorState, PageHeader, SectionLabel, Skeleton } from "../components/ui";

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/**
 * The quick look, made for a phone: what's costing conversions right now across every app the
 * person can see, the same content as Monday's digest, and where to tap next. The installed app
 * opens here.
 */
export function Today() {
  const today = useQuery({ queryKey: ["today"], queryFn: () => api<TodayData>("/me/today"), refetchOnWindowFocus: true, staleTime: 60_000 });
  const tracked = useRef(false);

  usePageTitle("Today");

  useEffect(() => {
    if (!today.data || tracked.current) return;
    tracked.current = true;
    track("today_viewed", { apps: today.data.apps.length, cases: today.data.apps.reduce((n, a) => n + a.cases.length, 0) });
  }, [today.data]);

  const d = today.data;
  const openCases = d?.apps.reduce((n, a) => n + a.cases.length, 0) ?? 0;
  const behind = d?.apps.reduce((n, a) => n + a.behind.length, 0) ?? 0;

  return (
    <>
      <PageHeader title="Today" subtitle="What's costing conversions right now, across every app you have access to." />
      <div className="max-w-2xl space-y-4" aria-busy={today.isPending}>
        {today.isError ? (
          <Card>
            <ErrorState title="Couldn't load today's summary" error={today.error} />
          </Card>
        ) : !d ? (
          <>
            <Skeleton className="h-24" />
            <Skeleton className="h-48" />
          </>
        ) : (
          <>
            <Card className="p-4">
              <p className="text-sm text-ink-2">{d.lost > 0 ? "Estimated lost conversions" : "Losing conversions"}</p>
              <p className="mt-0.5 text-2xl font-semibold tracking-tight text-ink">
                {d.lost > 0 ? `${d.apps.some((a) => a.cases.some((c) => c.upTo && c.money)) ? "Up to" : "About"} ${fmtMoney(d.lost)}` : openCases || behind ? `${plural(openCases + behind, "problem")} to look at` : "Nothing right now"}
              </p>
              <p className="mt-1 text-sm text-ink-2">
                {[openCases && plural(openCases, "open case"), behind && `${plural(behind, "task")} where people using an accessibility setting fall behind`].filter(Boolean).join(" · ") ||
                  "No open cases and no tasks falling behind."}
              </p>
              <p className="mt-2 text-xs text-ink-3">
                Updated <time dateTime={new Date(d.generatedAt).toISOString()}>{Date.now() - d.generatedAt < 60_000 ? "just now" : fmtAgo(d.generatedAt)}</time>
              </p>
            </Card>

            <PushPrompt />

            {d.apps.length === 0 ? (
              <Card>
                <Empty illustration={<Drawing name="allClear" />} title="All quiet">
                  No open cases, friction hotspots or alerts this week. When something starts costing conversions, it shows up here.
                </Empty>
              </Card>
            ) : (
              d.apps.map((a) => <AppToday key={a.appId} a={a} />)
            )}
          </>
        )}
      </div>
    </>
  );
}

function AppToday({ a }: { a: TodayApp }) {
  const lost = a.cases.reduce((s, c) => s + (c.money ?? 0), 0);
  const upTo = a.cases.some((c) => c.upTo && c.money);
  const heading = a.workspace && a.workspace !== a.appName ? `${a.appName} (${a.workspace})` : a.appName;
  return (
    <Card aria-labelledby={`today-${a.appId}`}>
      <CardHeader
        title={<span id={`today-${a.appId}`}>{heading}</span>}
        subtitle={[lost > 0 && `${upTo ? "Up to" : "About"} ${fmtMoney(lost)} lost`, a.alerts > 0 && `${plural(a.alerts, "alert")} this week`].filter(Boolean).join(" · ") || undefined}
      />
      {(a.cases.length > 0 || a.behind.length > 0) && (
        <Group title="Losing conversions">
          {a.cases.slice(0, 5).map((c) => (
            <Row key={c.path} to={c.path} detail={[c.money ? `${c.upTo ? "Up to" : "About"} ${fmtMoney(c.money)} lost` : null, c.affected ? `${plural(c.affected, "person", "people")} affected` : null].filter(Boolean).join(" · ")}>
              {c.title}
            </Row>
          ))}
          {a.behind.slice(0, 3).map((b) => (
            <Row key={b.path} to={b.path} detail={b.sentence}>
              “{b.task}”
            </Row>
          ))}
        </Group>
      )}
      {a.hotspot && (
        <Group title="Top friction">
          <Row to={a.hotspot.path}>{a.hotspot.title}</Row>
        </Group>
      )}
      {a.fixed.length > 0 && (
        <Group title={`${CASE_STATUS_LABEL.closed} this week`}>
          {a.fixed.slice(0, 3).map((f) => (
            <Row key={f.path} to={f.path}>
              {f.title}
            </Row>
          ))}
        </Group>
      )}
    </Card>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <SectionLabel as="h3" className="border-t border-line px-4 pt-3 pb-1">
        {title}
      </SectionLabel>
      <ul className="divide-y divide-line">{children}</ul>
    </div>
  );
}

/** One tappable row, at least 44px tall. */
function Row({ to, detail, children }: { to: string; detail?: string; children: React.ReactNode }) {
  return (
    <CardRowLink to={to} className="min-h-11 py-3">
      <span className="min-w-0">
        <span className="block text-ink">{children}</span>
        {detail && <span className="mt-0.5 block text-xs text-ink-3">{detail}</span>}
      </span>
      <ChevronRight size={16} aria-hidden className="shrink-0 text-ink-3" />
    </CardRowLink>
  );
}

/** A nudge to get these as push notifications, until push is on for this device. */
function PushPrompt() {
  const push = usePushStatus();
  if (!push.data?.enabled) return null;
  const mine = thisDeviceId();
  if (mine && push.data.devices.some((d) => d.id === mine)) return null;
  return (
    <Callout tone="info" icon={<BellRing size={14} aria-hidden />}>
      {pushSupported() ? "Get an alert on this device the moment a task starts losing conversions." : "Get alerts on your phone the moment a task starts losing conversions."}{" "}
      <Link to="/settings#notifications" className="font-medium">
        Turn on push notifications
      </Link>
    </Callout>
  );
}
